import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BookCard from "../components/BookCard";
import Cover from "../components/BookCover";
import { fetchCategories } from "../api";

const CATEGORY_ALIASES = {
    "B.Com": ["B.Com", "B.Com, M.Com, LL.B., MBA"],
    "M.Com": ["M.Com", "B.Com, M.Com, LL.B., MBA"],
    "LL.B.": ["LL.B.", "B.Com, M.Com, LL.B., MBA", "Students - LL.B., MBA, etc"],
    "MBA": ["MBA", "B.Com, M.Com, LL.B., MBA", "Students - LL.B., MBA, etc"],
    "Students - LL.B., MBA, etc": ["Students - LL.B., MBA, etc", "B.Com, M.Com, LL.B., MBA"],
    "CA Inter/CPT": ["CA Inter/CPT", "CA Inter/CPT, CS Exec, CMA Inter"],
    "CS Exec": ["CS Exec", "CS Executive", "CA Inter/CPT, CS Exec, CMA Inter"],
    "CS Executive": ["CS Exec", "CS Executive", "CA Inter/CPT, CS Exec, CMA Inter"],
    "CMA Inter": ["CMA Inter", "CA Inter/CPT, CS Exec, CMA Inter"],
    "CA Inter/CPT, CS Exec, CMA Inter": ["CA Inter/CPT", "CS Exec", "CS Executive", "CMA Inter", "CA Inter/CPT, CS Exec, CMA Inter"],
    "CA Final": ["CA Final", "CA,CS, CMA Final"],
    "CS Final": ["CS Final", "CA,CS, CMA Final"],
    "CMA Final": ["CMA Final", "CA,CS, CMA Final"],
    "CA,CS, CMA Final": ["CA Final", "CS Final", "CMA Final", "CA,CS, CMA Final"],
    "Corporate & Commercial Laws": ["Corporate & Commercial Laws", "Corporate and Commercial Laws"],
    "Corporate and Commercial Laws": ["Corporate & Commercial Laws", "Corporate and Commercial Laws"],
    "GST & Indirect Tax": ["GST & Indirect Tax", "GST, Service Tax & Indirect Tax"],
    "GST, Service Tax & Indirect Tax": ["GST & Indirect Tax", "GST, Service Tax & Indirect Tax"],
    "Banking & Finance": ["Banking & Finance", "Banking and Finance"],
    "Banking and Finance": ["Banking & Finance", "Banking and Finance"],
};

export default function StorePage({ books = [], onView, cart, addCart }) {
    const [cat, setCat] = useState("all");
    const [sort, setSort] = useState("popular");
    const [searchQuery, setSearchQuery] = useState("");
    const [priceRange, setPriceRange] = useState(null);
    const [view, setView] = useState("grid");
    const [page, setPageN] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const PER = 12;

    const { search } = useLocation();
    const navigate = useNavigate();

    // Sync state with URL cat parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(search);
        const urlCats = urlParams.getAll("cat").filter(Boolean);
        if (urlCats.length > 0) {
            setCat(urlCats.length === 1 ? urlCats[0] : "__multi__");
        } else {
            setCat("all");
        }
        setPageN(1);
    }, [search]);

    const handleCatChange = (newCat) => {
        if (newCat === "all") {
            navigate("/store");
        } else {
            navigate(`/store?cat=${encodeURIComponent(newCat)}`);
        }
    };

    const maxBookPrice = Math.max(2000, ...books.map(b => b.price || 0));
    const currentMax = priceRange ? priceRange[1] : maxBookPrice;

    const getIcon = (cat) => {
        if (!cat) return "📚";
        const c = cat.toLowerCase();
        if (c.includes("gst")) return "📜";
        if (c.includes("tax")) return "📊";
        if (c.includes("company") || c.includes("corporate")) return "🏢";
        if (c.includes("insolvency") || c.includes("bankruptcy")) return "⚖️";
        if (c.includes("customs") || c.includes("export") || c.includes("import")) return "🚢";
        if (c.includes("fema") || c.includes("foreign")) return "🌍";
        if (c.includes("sebi") || c.includes("securit")) return "📈";
        if (c.includes("audit") || c.includes("account")) return "🔍";
        if (c.includes("banking") || c.includes("finance")) return "🏦";
        return "📚";
    };

    const [CATS, setCATS] = useState([{ id: "all", label: "All Books", icon: "📚", count: books.length }]);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await fetchCategories();
                const formatted = data
                    .filter(c => c.id !== 'all')
                    .map(c => ({
                        ...c,
                        id: c.label, // use label as id to match book.cat
                        icon: getIcon(c.label)
                    }));
                
                setCATS([
                    { id: "all", label: "All Books", icon: "📚", count: books.length },
                    ...formatted
                ]);
            } catch (err) {
                console.error("Failed to load categories in store", err);
            }
        };
        loadCategories();
    }, [books.length]);

    const stars = r => "★".repeat(Math.floor(r)) + "☆".repeat(5 - Math.floor(r));
    const fmtINR = n => "₹" + n.toLocaleString("en-IN");

    const selectedCats = new URLSearchParams(search).getAll("cat").filter(Boolean);
    const expandedSelectedCats = selectedCats.flatMap((selectedCat) => CATEGORY_ALIASES[selectedCat] || [selectedCat]);
    const activeCategories = expandedSelectedCats.length > 0 ? [...new Set(expandedSelectedCats)] : ["all"];

    const filtered = books
        .filter(b =>
            (activeCategories.includes("all") || activeCategories.includes(b.cat)) &&
            (searchQuery === "" || b.title.toLowerCase().includes(searchQuery.toLowerCase()) || b.author.toLowerCase().includes(searchQuery.toLowerCase())) &&
            b.price >= 0 && b.price <= currentMax
        )
        .sort((a, b) => {
            if (sort === "rating") return b.rating - a.rating;
            if (sort === "priceLow") return a.price - b.price;
            if (sort === "priceHigh") return b.price - a.price;
            if (sort === "newest") return (b.new ? 1 : 0) - (a.new ? 1 : 0);
            return b.reviews - a.reviews;
        });

    const paginated = filtered.slice(0, page * PER);

    const sideboxStyle = {
        background: "white", borderRadius: 18,
        border: "1.5px solid #f0d0d0", overflow: "hidden", marginBottom: 20,
        boxShadow: "0 2px 12px rgba(192,57,43,0.06)"
    };

    const sideHeadStyle = {
        padding: "14px 20px", borderBottom: "1.5px solid #f0d0d0",
        fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 14,
        color: "#1a0505", background: "#fff8f8",
        display: "flex", alignItems: "center", gap: 8
    };

    return (
        <div style={{ minHeight: "100vh", background: "#FDF5F5" }}>
            {/* Store header */}
            <div className="store-header" style={{
                background: "linear-gradient(135deg, #2C0A0A 0%, #1A0505 60%, #250B0B 100%)",
                padding: "40px 72px", position: "relative", overflow: "hidden"
            }}>
                <style>{`
                    @media (max-width: 1024px) {
                        .store-header { padding: 32px 24px !important; }
                        .store-header-flex { flex-direction: column !important; align-items: flex-start !important; gap: 20px !important; }
                        .store-search-wrap { width: 100% !important; }
                        .store-main-flex { flex-direction: column !important; padding: 0 24px 40px !important; }
                        .store-sidebar { 
                            position: fixed !important; top: 0; left: 0; width: 280px !important; height: 100vh !important;
                            background: white; z-index: 1100; padding: 24px !important; overflow-y: auto;
                            transform: translateX(-100%); transition: transform 0.3s ease;
                            box-shadow: 10px 0 30px rgba(0,0,0,0.1);
                        }
                        .store-sidebar.open { transform: translateX(0); }
                        .store-sidebar-overlay {
                            position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1090;
                            display: none;
                        }
                        .store-sidebar-overlay.open { display: block; }
                        .mobile-filter-bar { display: flex !important; }
                    }
                    .mobile-filter-bar { display: none; justify-content: space-between; align-items: center; margin-bottom: 16px; }
                `}</style>
                {/* Glow */}
                <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: "radial-gradient(circle at 80% 50%, rgba(192,57,43,0.2) 0%, transparent 60%)",
                    pointerEvents: "none"
                }} />
                {/* Top accent line */}
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, transparent, #C0392B 30%, #E8523A 50%, #C0392B 70%, transparent)" }} />

                <div className="store-header-flex" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", position: "relative", zIndex: 1 }}>
                    <div>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: "rgba(192,57,43,0.2)", border: "1px solid rgba(192,57,43,0.35)",
                            borderRadius: 50, padding: "4px 12px", marginBottom: 12
                        }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#E8523A", display: "block" }} />
                            <span style={{ color: "#E8523A", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em" }}>BROWSE & DISCOVER</span>
                        </div>
                        <h1 style={{
                            fontFamily: "'Playfair Display',serif", fontSize: 38,
                            fontWeight: 800, color: "white", letterSpacing: "-0.02em"
                        }}>Book Store</h1>
                        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, marginTop: 4 }}>
                            {filtered.length} books across {CATS.length - 1} categories
                        </p>
                    </div>
                    <div className="store-search-wrap" style={{ position: "relative" }}>
                        <input
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search books..."
                            style={{
                                width: 260, height: 42, paddingLeft: 38, borderRadius: 50,
                                border: "1.5px solid rgba(192,57,43,0.4)",
                                background: "rgba(255,255,255,0.08)",
                                color: "white", fontFamily: "'Nunito Sans',sans-serif",
                                fontSize: 13, outline: "none",
                                transition: "border-color 0.2s"
                            }}
                            onFocus={e => e.currentTarget.style.borderColor = "#E8523A"}
                            onBlur={e => e.currentTarget.style.borderColor = "rgba(192,57,43,0.4)"}
                        />
                        <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.45)" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="store-main-flex" style={{ display: "flex", padding: "0 72px 64px" }}>
                {/* Sidebar Overlay */}
                <div className={`store-sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

                {/* Sidebar */}
                <div className={`store-sidebar ${sidebarOpen ? 'open' : ''}`} style={{ width: 240, flexShrink: 0, paddingTop: 28, paddingRight: 28 }}>
                    {sidebarOpen && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <span style={{ fontWeight: 800, color: "#C0392B" }}>FILTERS</span>
                            <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", fontSize: 20, color: "#C0392B" }}>✕</button>
                        </div>
                    )}

                    {/* Categories */}
                    <div style={sideboxStyle}>
                        <div style={sideHeadStyle}>
                            <span style={{ width: 14, height: 2, background: "#C0392B", borderRadius: 2 }} />
                            Categories
                        </div>
                        {CATS.map(c => (
                            <button
                                key={c.id}
                                onClick={() => handleCatChange(c.id)}
                                style={{
                                    width: "100%", textAlign: "left", padding: "11px 20px", border: "none",
                                    background: cat === c.id
                                        ? "linear-gradient(90deg, #C0392B, #96221A)"
                                        : "transparent",
                                    color: cat === c.id ? "white" : "#7a4a4a",
                                    fontFamily: "'Nunito Sans',sans-serif", fontSize: 13,
                                    fontWeight: cat === c.id ? 700 : 400,
                                    cursor: "pointer", transition: "all 0.15s",
                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                    borderBottom: "1px solid #fae8e8",
                                }}
                                onMouseEnter={e => { if (cat !== c.id) e.currentTarget.style.background = "#fff0f0"; }}
                                onMouseLeave={e => { if (cat !== c.id) e.currentTarget.style.background = "transparent"; }}
                            >
                                <span>{c.icon} {c.label}</span>
                                <span style={{
                                    fontSize: 11, opacity: 0.8,
                                    background: cat === c.id ? "rgba(255,255,255,0.2)" : "#fae8e8",
                                    color: cat === c.id ? "white" : "#C0392B",
                                    padding: "1px 7px", borderRadius: 50, fontWeight: 700
                                }}>{c.count}</span>
                            </button>
                        ))}
                    </div>

                    {/* Sort */}
                    <div style={sideboxStyle}>
                        <div style={sideHeadStyle}>
                            <span style={{ width: 14, height: 2, background: "#C0392B", borderRadius: 2 }} />
                            Sort By
                        </div>
                        {[["popular", "Most Popular"], ["rating", "Highest Rated"], ["priceLow", "Price: Low to High"], ["priceHigh", "Price: High to Low"], ["newest", "Newest First"]].map(([v, l]) => (
                            <button
                                key={v}
                                onClick={() => setSort(v)}
                                style={{
                                    width: "100%", textAlign: "left", padding: "10px 20px", border: "none",
                                    background: sort === v ? "#fff0f0" : "transparent",
                                    color: sort === v ? "#C0392B" : "#7a4a4a",
                                    fontFamily: "'Nunito Sans',sans-serif", fontSize: 13,
                                    fontWeight: sort === v ? 700 : 400,
                                    cursor: "pointer", transition: "all 0.15s",
                                    display: "flex", alignItems: "center", gap: 8,
                                    borderBottom: "1px solid #fae8e8"
                                }}
                            >
                                {sort === v && <span style={{ color: "#C0392B" }}>✓</span>} {l}
                            </button>
                        ))}
                    </div>

                </div>

                {/* Main grid */}
                <div style={{ flex: 1, paddingTop: 28 }}>
                    <div className="mobile-filter-bar">
                        <button 
                            onClick={() => setSidebarOpen(true)}
                            style={{
                                background: "white", border: "1.5px solid #f0d0d0", borderRadius: 8,
                                padding: "8px 16px", fontSize: 13, fontWeight: 700, color: "#C0392B",
                                display: "flex", alignItems: "center", gap: 8
                            }}
                        >
                            <span>🔍</span> Filters & Categories
                        </button>
                        <span style={{ fontSize: 12, color: "#c07070" }}>{filtered.length} books</span>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <span style={{ fontSize: 13, color: "#c07070", fontWeight: 600 }}>
                            {filtered.length} books found
                        </span>
                        <div style={{ display: "flex", gap: 6 }}>
                            {[["grid", "⊞"], ["list", "≡"]].map(([v, icon]) => (
                                <button
                                    key={v}
                                    onClick={() => setView(v)}
                                    style={{
                                        width: 36, height: 36, borderRadius: 8,
                                        border: `1.5px solid ${view === v ? "#C0392B" : "#f0d0d0"}`,
                                        background: view === v ? "#C0392B" : "white",
                                        color: view === v ? "white" : "#c07070",
                                        cursor: "pointer", fontSize: 16, transition: "all 0.15s"
                                    }}
                                >{icon}</button>
                            ))}
                        </div>
                    </div>

                    {view === "grid" ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(215px,1fr))", gap: 22 }}>
                            {paginated.map((b, i) => (
                                <div key={b._id} style={{ animation: `fadeUp 0.4s ${(i % 12) * 0.04}s both` }}>
                                    <BookCard book={b} onView={onView} cart={cart} addCart={addCart} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {paginated.map(b => (
                                <div
                                    key={b._id}
                                    onClick={() => onView(b)}
                                    style={{
                                        background: "white", borderRadius: 16,
                                        border: "1.5px solid #f0d0d0",
                                        padding: 18, display: "flex", gap: 20,
                                        cursor: "pointer", transition: "all 0.2s",
                                        boxShadow: "0 2px 8px rgba(192,57,43,0.05)"
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(192,57,43,0.12)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#f0d0d0"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(192,57,43,0.05)"; }}
                                >
                                    <Cover book={b} w={80} h={112} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div>
                                                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 2, color: "#1a0505" }}>{b.title}</h3>
                                                <p style={{ fontSize: 12, color: "#c07070", marginBottom: 6 }}>{b.author} · {b.pages} pages</p>
                                                <div style={{ fontSize: 12, color: "#D4A847" }}>{stars(b.rating)} <span style={{ color: "#c07070" }}>({b.reviews?.toLocaleString()})</span></div>
                                            </div>
                                            <div style={{ textAlign: "right" }}>
                                                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800, color: "#C0392B" }}>{fmtINR(b.price)}</div>
                                                <div style={{ fontSize: 11, color: "#d0a0a0", textDecoration: "line-through" }}>{fmtINR(b.orig)}</div>
                                                {b.badge && (
                                                    <span style={{
                                                        background: "linear-gradient(135deg, #C0392B, #96221A)",
                                                        color: "white", fontSize: 10, fontWeight: 800,
                                                        padding: "2px 9px", borderRadius: 50,
                                                        display: "inline-block", marginTop: 4,
                                                        boxShadow: "0 2px 8px rgba(192,57,43,0.3)"
                                                    }}>{b.badge}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={e => { e.stopPropagation(); addCart(b); }}
                                        style={{
                                            alignSelf: "center",
                                            background: cart.some(c => c._id === b._id)
                                                ? "linear-gradient(135deg, #27ae60, #1e8449)"
                                                : "linear-gradient(135deg, #C0392B, #96221A)",
                                            color: "white", padding: "9px 20px", fontSize: 13,
                                            flexShrink: 0, border: "none", borderRadius: 8,
                                            cursor: "pointer", fontFamily: "'Nunito Sans',sans-serif",
                                            fontWeight: 700, transition: "all 0.2s",
                                            boxShadow: "0 3px 12px rgba(192,57,43,0.3)"
                                        }}
                                    >
                                        {cart.some(c => c._id === b._id) ? "✓ In Cart" : "Add to Cart"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Load more */}
                    {paginated.length < filtered.length && (
                        <div style={{ textAlign: "center", marginTop: 36 }}>
                            <button
                                onClick={() => setPageN(p => p + 1)}
                                style={{
                                    padding: "13px 36px", fontSize: 15, fontWeight: 700,
                                    background: "transparent",
                                    border: "2px solid #C0392B", color: "#C0392B",
                                    borderRadius: 10, cursor: "pointer",
                                    fontFamily: "'Nunito Sans',sans-serif", transition: "all 0.2s"
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = "#C0392B"; e.currentTarget.style.color = "white"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#C0392B"; }}
                            >
                                Load More Books ({filtered.length - paginated.length} remaining)
                            </button>
                        </div>
                    )}

                    {/* Empty state */}
                    {filtered.length === 0 && (
                        <div style={{ textAlign: "center", padding: "80px 0", color: "#c07070" }}>
                            <div style={{ fontSize: 56, marginBottom: 16 }}>📚</div>
                            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, marginBottom: 8, color: "#1a0505" }}>No books found</h3>
                            <p>Try adjusting your filters or search query</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
